import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { projectsApi } from '../lib/api'
import { projectsCache } from '../lib/cache'

const PageContainer = styled.div`
  padding-top: 110px;
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 60%);
`

const Hero = styled.section`
  position: relative;
  margin: 0 1rem;
  border-radius: 16px;
  overflow: hidden;
  min-height: 320px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #fff;
  background: linear-gradient(120deg, rgba(15, 23, 42, 0.9), rgba(30, 64, 175, 0.75));

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => (props.$image ? `url('${props.$image}') center/cover no-repeat` : 'none')};
    opacity: ${props => (props.$image ? 0.28 : 0)};
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 860px;
  padding: 2rem 1.2rem;

  h1 {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3.2rem);
    font-family: 'Playfair Display', serif;
  }

  p {
    margin: 1rem 0 0;
    line-height: 1.7;
    font-family: 'Lato', sans-serif;
    color: #e2e8f0;
  }
`

const Wrapper = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.2rem 3rem;
`

const Info = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  font-family: 'Lato', sans-serif;
`

const LoadingPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 1rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  font-size: 0.85rem;
  font-family: 'Lato', sans-serif;
`

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #2563eb;
  animation: pulse 1s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 0.35;
      transform: scale(0.9);
    }
    50% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0.35;
      transform: scale(0.9);
    }
  }
`

const Error = styled.div`
  margin-bottom: 1rem;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
`

const PostsGrid = styled.div`
  display: grid;
  gap: 1rem;
`

const PostCard = styled.article`
  border: 1px solid #dbeafe;
  border-radius: 14px;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);

  h2 {
    margin: 0;
    color: #0f172a;
    font-family: 'Playfair Display', serif;
  }

  p {
    color: #475569;
    line-height: 1.7;
    font-family: 'Lato', sans-serif;
  }
`

const ImageStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.7rem;
  margin-top: 0.9rem;

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 10px;
    background: #e2e8f0;
  }
`

const VisitLink = styled.a`
  display: inline-block;
  margin-top: 0.4rem;
  color: #0369a1;
  font-weight: 700;
  text-decoration: none;
`

function ProjectsPage() {
  const [header, setHeader] = useState({ title: 'Projects', description: '', image: '' })
  const [posts, setPosts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [cacheState, setCacheState] = useState('No cache available yet.')
  const [imagesTotal, setImagesTotal] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(0)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        const [cachedHeader, cachedPosts] = await Promise.all([
          projectsCache.readHeader(),
          projectsCache.readPosts()
        ])

        if (!mounted) return

        if (cachedHeader.value) {
          setHeader(cachedHeader.value)
        }

        if (cachedPosts.value && Array.isArray(cachedPosts.value.items)) {
          setPosts(cachedPosts.value.items)
        }

        if (cachedHeader.value || cachedPosts.value) {
          setIsLoading(false)
          const fresh = cachedHeader.isFresh && cachedPosts.isFresh
          setCacheState(
            fresh
              ? 'Fresh cache loaded. Syncing latest updates in background.'
              : 'Showing cached project data while refreshing.'
          )
        }
      } catch (_error) {
        // cache read should not block API request
      }

      try {
        const [headerPayload, postsPayload] = await Promise.all([
          projectsApi.getHeader(),
          projectsApi.listPosts({ page: 1, limit: 50 })
        ])

        if (!mounted) return

        setHeader(headerPayload || { title: 'Projects', description: '', image: '' })
        setPosts(Array.isArray(postsPayload.items) ? postsPayload.items : [])
        setError('')
        setCacheState('Fresh data loaded from API.')
        setIsLoading(false)

        await Promise.all([
          projectsCache.writeHeader(headerPayload),
          projectsCache.writePosts(postsPayload)
        ])
      } catch (apiError) {
        if (!mounted) return
        setError(apiError.message || 'Failed to load projects')
        setIsLoading(false)
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [])

  const visiblePosts = useMemo(() => posts.filter(Boolean), [posts])

  useEffect(() => {
    const total = visiblePosts.reduce((sum, post) => sum + (Array.isArray(post.images) ? post.images.length : 0), 0)
    setImagesTotal(total)
    setImagesLoaded(0)
  }, [visiblePosts])

  const handleImageSettled = () => {
    setImagesLoaded(prev => prev + 1)
  }

  const settledImages = Math.min(imagesLoaded, imagesTotal)
  const pendingImages = Math.max(imagesTotal - settledImages, 0)

  return (
    <PageContainer>
      <Hero $image={header.image}>
        <HeroContent>
          <h1>{header.title || 'Projects'}</h1>
          <p>{header.description || 'Discover our latest engineering projects and outcomes.'}</p>
        </HeroContent>
      </Hero>

      <Wrapper>
        <Info>
          {isLoading ? 'Loading projects...' : `${visiblePosts.length} posts`} • {cacheState}
        </Info>
        <Info>Images loaded: {settledImages}/{imagesTotal}</Info>

        {isLoading || pendingImages > 0 ? (
          <LoadingPill>
            <Dot />
            {isLoading ? 'Loading project data...' : `Loading image assets (${pendingImages} left)...`}
          </LoadingPill>
        ) : null}

        {error ? <Error>{error}</Error> : null}

        <PostsGrid>
          {visiblePosts.map(post => (
            <PostCard key={post._id}>
              <h2>{post.title}</h2>
              <p>{post.description || 'No description provided.'}</p>

              {post.link ? (
                <VisitLink href={post.link} target="_blank" rel="noreferrer">
                  Visit project link
                </VisitLink>
              ) : null}

              {Array.isArray(post.images) && post.images.length ? (
                <ImageStrip>
                  {post.images.map((image, index) => (
                    <img
                      key={`${post._id}-img-${index}`}
                      src={image}
                      alt={`${post.title} ${index + 1}`}
                      loading="lazy"
                      onLoad={handleImageSettled}
                      onError={handleImageSettled}
                    />
                  ))}
                </ImageStrip>
              ) : null}
            </PostCard>
          ))}
        </PostsGrid>
      </Wrapper>
    </PageContainer>
  )
}

export default ProjectsPage
